import { useCallback } from 'react'
import type { LazyQueryHookOptions, LazyQueryResultTuple, OperationVariables } from '@apollo/client'
import { SearchOptionWithUI } from '@broker-crm-common/GlobalSearch/GlobalSearch.types'
import { DEFAULT_EMPTY_OPTION } from '@broker-crm-common/GlobalSearch/GlobalSearchOption/GlobalSearchOption.helpers'

import { CaseStatus, useGetSearchResultsLazyQuery } from '@acre/graphql'
import type { GetSearchResultsQueryResult, GetSearchResultsQueryVariables, SearchResultItem } from '@acre/graphql'

export type UseGlobalSearchProps<
  Query extends UseQueryHook,
  Variables extends OperationVariables = OperationVariables,
  Data = ReturnType<Query>,
> = {
  query: Query
  initialQueryVariables?: Variables
  getData?: (response: Data) => any[]
}

export type UseQueryHook<Data extends any = any, Variables extends OperationVariables = any> = (
  baseOptions: LazyQueryHookOptions<Data, Variables>,
) => LazyQueryResultTuple<Data, Variables>

/**
 * Custom hook for performing global search operations in combination with AutocompleteField
 * Manages search queries, pagination, and data transformation
 */
const useGlobalSearch = <Data>(
  params: UseGlobalSearchProps<typeof useGetSearchResultsLazyQuery, GetSearchResultsQueryVariables, Data>,
) => {
  const { query: useQueryProp } = params

  /**
   * Creates query variables object from search parameters
   */
  const getQueryVariables = useCallback(
    (data: {
      value?: string | number
      cursor?: number
    }): {
      variables: GetSearchResultsQueryVariables
    } => {
      const { value, cursor } = data

      return {
        variables: {
          searchFragment: encodeURIComponent(value ?? ''),
          bookmark: cursor ?? 0,
        },
      }
    },
    [],
  )

  const [getSearchResults, { loading, fetchMore, networkStatus }] = useQueryProp({
    notifyOnNetworkStatusChange: true,
    // the combination of cache and infinite scroll is not working properly so disable the cache for now
    fetchPolicy: 'no-cache',
  })

  const loadingStatus = loading || networkStatus === 4 /* refetch */ || networkStatus === 1 /* loading */

  /**
   * Extracts the pagination cursor from response data
   * Return null if there's no bookmark or we've reached the end
   */
  const getPageCursor = (data: any): number | null => {
    const bookmark = data?.data?.search?.bookmark
    if (!bookmark || bookmark === data?.data?.search?.total_results) return null

    return bookmark
  }

  /**
   * Extracts and flattens search result items from the response data
   */
  const getOptions = useCallback((options: GetSearchResultsQueryResult): SearchOptionWithUI[] => {
    const results = (options?.data?.search?.results ?? []) as SearchResultItem[]

    return results.flatMap((option) => {
      const clientNameTag =
        option.organisation_name ||
        [option.first_name, option.last_name].filter(Boolean).join(' ') ||
        DEFAULT_EMPTY_OPTION

      // Client option with clientName
      const items: SearchOptionWithUI[] = [
        {
          ...option,
          clientNameTag,
          Cases: undefined,
        } as SearchOptionWithUI,
      ]

      // Cases options with clientName
      if (option.Cases) {
        const validCases = option.Cases.filter((caseItem) => caseItem.case_status !== CaseStatus.InvalidStatus).map(
          (caseItem) => ({
            ...caseItem,
            clientNameTag,
          }),
        )

        items.push(...validCases)
      }

      return items
    })
  }, [])

  /**
   * Loads more search results for pagination
   */
  const loadMore = useCallback(
    async (variables: { variables: GetSearchResultsQueryVariables }) => {
      const result = await fetchMore({
        variables: variables.variables,
        // we are using a `no-cache` fetch policy so we need to tell apollo how to merge the results
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          return {
            ...prev,
            search: {
              ...prev.search,
              results: [...(prev.search?.results ?? []), ...(fetchMoreResult.search?.results ?? [])],
            },
          }
        },
      })

      return result
    },
    [fetchMore],
  )

  return {
    loading: loadingStatus,
    loadData: getSearchResults,
    loadMore,
    getQueryVariables,
    getPageCursor,
    getOptions,
  }
}

export default useGlobalSearch
